import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  buildDraftPayload,
  captureScheduleDraft,
  draftFingerprint,
  type DraftAssignmentInput,
} from "@/lib/scheduleDraft";

/** Idle time after the last edit before an autosnapshot is written. */
export const AUTOSAVE_DELAY_MS = 20_000;

/** Fingerprint of an empty schedule — used to detect the async-load transition. */
const EMPTY_FINGERPRINT = draftFingerprint([]);

export type AutosaveStatus = "idle" | "saving" | "saved" | "error";

interface UseScheduleAutosaveArgs {
  briefingId: string | null | undefined;
  assignments: DraftAssignmentInput[];
  /** Off until the briefing exists (autosave needs a real briefing id). */
  enabled?: boolean;
}

/**
 * Periodically captures the schedule editor's current draft as a kind='auto'
 * history snapshot — a recoverable copy that is NOT published to the crew.
 * Fires ~AUTOSAVE_DELAY_MS after edits settle, and only when the draft
 * actually changed since the last capture (fingerprint compare).
 *
 * Guards against autosaving data the producer didn't actually edit:
 *  - a baseline is established on first pass so opening a schedule never
 *    autosaves;
 *  - the baseline is re-established when assignments load in asynchronously
 *    (empty -> populated) and when the briefing changes, so neither a late
 *    data load nor switching productions is mistaken for an edit;
 *  - an INCOMPLETE draft (some assignments dropped because their crew member
 *    isn't saved yet) is never snapshotted — restoring a partial draft would
 *    deactivate those crew's real assignments. The next manual save captures
 *    the complete state.
 */
export function useScheduleAutosave({
  briefingId,
  assignments,
  enabled = true,
}: UseScheduleAutosaveArgs): { status: AutosaveStatus } {
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const lastSavedFp = useRef<string | null>(null);
  const baselineBriefingId = useRef<string | null | undefined>(undefined);

  const { fingerprint, droppedUnsaved } = useMemo(() => {
    const { rows, droppedUnsaved } = buildDraftPayload(assignments);
    return { fingerprint: draftFingerprint(rows), droppedUnsaved };
  }, [assignments]);

  const active = !!briefingId && enabled;

  useEffect(() => {
    if (!active) return;

    // Re-baseline (without autosaving) when switching briefings — the previous
    // briefing's fingerprint must not be treated as an edit to this one.
    if (baselineBriefingId.current !== briefingId) {
      baselineBriefingId.current = briefingId;
      lastSavedFp.current = fingerprint;
      return;
    }

    // First pass for this briefing: establish the baseline, don't autosave.
    if (lastSavedFp.current === null) {
      lastSavedFp.current = fingerprint;
      return;
    }

    if (fingerprint === lastSavedFp.current) return;

    // Async data load: the baseline was the empty schedule and real data just
    // arrived. That's a load, not an edit — re-baseline rather than autosave.
    if (lastSavedFp.current === EMPTY_FINGERPRINT && fingerprint !== EMPTY_FINGERPRINT) {
      lastSavedFp.current = fingerprint;
      return;
    }

    // Incomplete draft (unsaved crew present): don't even schedule — the skip
    // rule itself lives in captureScheduleDraft (its single owner); this just
    // avoids a pointless timer + status flicker when we already know it'd skip.
    if (droppedUnsaved > 0) return;

    const timer = setTimeout(async () => {
      setStatus("saving");
      try {
        const { captured } = await captureScheduleDraft(
          (name, opts) => supabase.functions.invoke(name, opts),
          briefingId!,
          assignments,
        );
        if (captured) lastSavedFp.current = fingerprint;
        setStatus("saved");
      } catch (err) {
        // Autosave is best-effort — a failure must be invisible to the flow.
        // The producer can always save manually; the error is logged only.
        console.error("[autosave] snapshot skipped:", err);
        setStatus("error");
      }
    }, AUTOSAVE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [active, briefingId, fingerprint, droppedUnsaved, assignments]);

  return { status };
}
